<template>
  <v-container class="pa-0 ma-0" fluid>
    <v-card elevation="0" min-width="100%">
      <common-data-table :headers="computedHeaders" collectionName="materials">
        <template v-slot:item="{ item, editItem }">
          <tr :key="item.id">
            <td>{{ item.name }}</td>
            <td>{{ item.category.name }}</td>
            <td>{{ item.location.name }}</td>
            <td>
              <editable-text
                type="number"
                :width="50"
                :value="item.quantity"
                @valueChanged="updateItemProperty(item, 'quantity', $event)"
              ></editable-text>
            </td>
            <td>
              <editable-text
                :value="item.unitPrice"
                :width="100"
                type="number"
                :prefix="item.currency.symbol"
                @valueChanged="updateItemProperty(item, 'unitPrice', $event)"
              ></editable-text>
            </td>
            <td>
              <feature name="warehouse" variant="write" data="materials">
                <v-icon small class="mr-2 ml-2" @click="editItem(item)"
                  >mdi-pencil</v-icon
                >
              </feature>
            </td>
          </tr>
        </template>

        <template v-slot:newItemPopup="{ close, item, formKey }">
          <common-editing-popup
            :title="item ? 'Edit item' : 'New item'"
            :saveBtnTitle="item ? 'Save item' : 'Create item'"
            @close="close()"
            collectionName="materials"
            :canSubmit="formValid"
            :useSetForData="true"
            :originalItem="item"
            :key="'material-edit-' + (item ? item.id : formKey)"
            :showDefaultSuccessMsg="false"
            @submit="addItem"
            @deleteItem="deleteItem"
            :defaultItem="{
              currency: {
                symbol: '£',
                abbrv: 'GBP',
                name: 'Great Britain Pound',
              },
            }"
          >
            <template v-slot:default="{ editedItem, extraData }">
              <add-material
                :formValid.sync="formValid"
                :editedItem="editedItem"
                :extraData="extraData"
              ></add-material>
            </template>
          </common-editing-popup>
        </template>
      </common-data-table>
    </v-card>
  </v-container>
</template>

<script>
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import AddMaterial from '@/views/AddMaterial.vue'
import { db, storage } from '@/firebaseConfig'
import { mapGetters, mapActions } from 'vuex'
import EditableText from '@/components/EditableText.vue'
import {
  hasAccessRightForCollection,
  AccessRightWrite,
} from '@/plugins/feature'
import { v4 as uuidv4 } from 'uuid'
import CommonDataTable from '@/components/CommonDataTable.vue'
import { objects } from '@/helpers'

export default {
  name: 'Inventory',
  components: {
    AddMaterial,
    EditableText,
    CommonEditingPopup,
    CommonDataTable,
  },
  data() {
    return {
      listeners: [],
      loading: true,
      formValid: false,
      submitting: false,
      actions: [
        {
          name: 'Add material',
          action: () => (this.addMaterialPageVisible = true),
        },
      ],
      headers: [
        {
          text: 'Material name',
          value: 'name',
        },
        {
          text: 'Category',
          value: 'category.name',
        },
        {
          text: 'Location',
          value: 'location.name',
        },
        {
          text: 'Qty',
          value: 'quantity',
        },
        {
          text: 'Unit price',
          value: 'unitPrice',
        },
      ],
      items: [],
    }
  },
  mounted() {
    this.loadData()
  },
  beforeDestroy() {
    // Unsubscribe from firestore listeners
    for (const idx in this.listeners) {
      this.listeners[idx]()
    }
    this.listeners = []
  },
  computed: {
    ...mapGetters(['companyId', 'newTableItemPopupVisible']),
    path() {
      return `companies/${this.companyId}/materials`
    },
    computedHeaders() {
      if (hasAccessRightForCollection('materials', AccessRightWrite)) {
        return [
          ...this.headers,
          { text: 'Edit', value: 'actions', sortable: false },
        ]
      }
      return this.headers
    },
  },
  methods: {
    ...mapActions([
      'hideNewTableItemPopup',
      'showNewTableItemPopup',
      'showError',
      'showSuccess',
      'showDeletionSuccess',
    ]),

    async updateItemProperty(item, propertyName, value) {
      const docRef = db.collection(this.path).doc(item.id)

      try {
        let update = {}
        update[propertyName] = value
        await docRef.update(objects.withUpdatedDataInfo(update))
      } catch (error) {
        console.error(error)
      }
    },
    async loadData() {
      this.loading = true

      // TODO: Add pagination, etc.
      var unsubscribe = db.collection(this.path).onSnapshot(
        (snapshot) => {
          const result = snapshot.docs.map((i) => {
            const result = i.data()
            Object.defineProperty(result, 'id', { value: i.id })
            return result
          })
          this.items = result
          this.loading = false
        },
        (error) => {
          console.error(error)
          this.loading = false
        }
      )
      this.listeners.push(unsubscribe)
    },
    getImageRefFromURL(imageURL) {
      const imageURLRef = unescape(/.*\/([^?]+)/.exec(imageURL)[1])
      return imageURLRef
    },
    async deleteRemovedImages(editedItem, extraData) {
      if (extraData?.imageToRemove) {
        const ref = this.getImageRefFromURL(extraData.imageToRemove)
        const imageRef = storage.ref().child(ref)
        await imageRef.delete()
        delete editedItem.imageURL
      }
    },
    async uploadNewImages(editedItem, extraData) {
      const file = extraData?.imagePreview?.file
      if (file) {
        const fileName = uuidv4()
        const imageRef = storage
          .ref()
          .child(`companies/${this.companyId}/materials/${fileName}`)
        const snapshot = await imageRef.put(file)
        const fileURL = await snapshot.ref.getDownloadURL()
        editedItem.imageURL = fileURL
      }
    },

    async addItem(editedItem, extraData) {
      this.submitting = true
      const path = `companies/${this.companyId}/materials`
      try {
        if (editedItem.id) {
          // Update
          await this.deleteRemovedImages(editedItem, extraData)
          await this.uploadNewImages(editedItem, extraData)

          await db
            .collection(path)
            .doc(editedItem.id)
            .set(objects.withUpdatedDataInfo(editedItem))
          this.showSuccess(
            `Item <b>${editedItem.name}</b> was added successfully`
          )
        } else {
          // Create new
          await this.uploadNewImages()
          await db.collection(path).add(objects.withCreatedDataInfo(editedItem))
          this.showSuccess(`Item <b>${editedItem.name}</b> has been added`)
        }

        this.hideNewTableItemPopup()
      } catch (error) {
        this.showError('<b>Failed to create material:</b>\n' + error)
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
    async deleteItem(editedItem, extraData) {
      this.submitting = true
      const path = `companies/${this.companyId}/materials`
      try {
        try {
          await this.deleteRemovedImages(editedItem, extraData)

          if (editedItem.imageURL) {
            const ref = this.getImageRefFromURL(editedItem.imageURL)
            const imageRef = storage.ref().child(ref)
            await imageRef.delete()
          }
        } catch (error) {
          console.warn('failed to delete images')
          console.error(error)
        }

        await db.collection(path).doc(editedItem.id).delete()
        this.showDeletionSuccess('<b>Success.</b> Item has been deleted.')
        this.hideNewTableItemPopup()
      } catch (error) {
        this.showError('<b>Failed.</b> Please try again later.')
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
